import {useCallback} from 'react';

import {useMutation, useQuery} from '@apollo/react-hooks';

import type {
    LeadResearchPromptQuery,
    LeadResearchPromptUpdateMutation,
    LeadResearchPromptUpdateMutationVariables,
} from 'nutshell-graphql-types';

import LEAD_RESEARCH_PROMPT_UPDATE_MUTATION from '../mutations/lead-research-prompt-update.graphql';
import LEAD_RESEARCH_PROMPT_QUERY from '../queries/lead-research-prompt.graphql';

export type AvailableField = {
    key: string;
    label: string;
};

type UseLeadResearchPromptResult = {
    selectedFields: string[];
    businessContext: string | null | undefined;
    idealCustomer: string | null | undefined;
    availableFields: AvailableField[];
    isLoading: boolean;
    isSaving: boolean;
    updateSettings: (settings: {
        selectedFields?: string[];
        businessContext?: string | null;
        idealCustomer?: string | null;
    }) => Promise<void>;
};

export function useLeadResearchPrompt(): UseLeadResearchPromptResult {
    const {data, loading: isLoading} = useQuery<LeadResearchPromptQuery>(
        LEAD_RESEARCH_PROMPT_QUERY
    );

    const [mutate, {loading: isSaving}] = useMutation<
        LeadResearchPromptUpdateMutation,
        LeadResearchPromptUpdateMutationVariables
    >(LEAD_RESEARCH_PROMPT_UPDATE_MUTATION);

    const updateSettings = useCallback(
        async (settings: {
            selectedFields?: string[];
            businessContext?: string | null;
            idealCustomer?: string | null;
        }) => {
            await mutate({
                variables: {
                    input: settings,
                },
                refetchQueries: [{query: LEAD_RESEARCH_PROMPT_QUERY}],
            });
        },
        [mutate]
    );

    const promptData = data && data.leadResearchPrompt;

    return {
        selectedFields: promptData ? promptData.selectedFields : [],
        businessContext: promptData && promptData.businessContext,
        idealCustomer: promptData && promptData.idealCustomer,
        availableFields: promptData ? promptData.availableFields : [],
        isLoading,
        isSaving,
        updateSettings,
    };
}
